import { getIllustrationSrc, getImageUploadSrc } from "@/domain/s3-urls";
import { DialogDisclosure } from "@ariakit/react";
import { DialogTrigger } from "../dialog-trigger";
import { Image } from "../image";
import { InlineButton } from "../inline-button";
import { darkKnight, iceElves, thumbSize } from "./illustrations";
import { MainImageSelectDialog } from "./MainImageDialog";
import * as css from "./style.css";
import { useStagedImage } from "./use-staged-image";

function IllustrationsPreview() {
  return (
    <DialogDisclosure
      className={css.illustrationsPreview.container}
      focusable={false}
      render={<div />}
    >
      <Image
        width={65}
        height={65}
        optimize
        className={css.illustrationsPreview.image}
        src={getIllustrationSrc(darkKnight)}
        alt=""
      />
      <Image
        width={65}
        height={65}
        optimize
        className={css.illustrationsPreview.image}
        src={getIllustrationSrc(iceElves)}
        alt=""
      />
    </DialogDisclosure>
  );
}

function ImagePicker(props: ReturnType<typeof useStagedImage>) {
  const { stagedImage } = props;

  const info = (
    <div style={{ flexGrow: 1 }}>
      <DialogDisclosure focusable={false} className={css.pickerLabel} render={<div />}>
        Main image
      </DialogDisclosure>

      <div className={css.pickerHint}>
        Select illustration or upload image
        {!!stagedImage && (
          <>
            {". "}
            <InlineButton onClick={() => props.setStagedImage(null)}>Clear</InlineButton>
          </>
        )}
      </div>
    </div>
  );

  if (!stagedImage) {
    return (
      <div className={css.picker}>
        <DialogDisclosure className={css.radio({ aspectRatio: "1/1", size: "small" })}>
          +
          <div className={css.selectionFrame({ thinBorder: true })} />
        </DialogDisclosure>

        {info}

        <IllustrationsPreview />
      </div>
    );
  }

  if (stagedImage.type === "ILLUSTRATION") {
    return (
      <div className={css.picker}>
        <DialogDisclosure className={css.radio({ aspectRatio: "1/1", size: "large" })}>
          <Image
            {...thumbSize}
            className={css.stagedImage({ fit: "contain" })}
            src={getIllustrationSrc(stagedImage.id)}
            optimize
          />

          <div className={css.selectionFrame({ thinBorder: true })} />
        </DialogDisclosure>

        {info}
      </div>
    );
  }

  const isStagedImageUpload = stagedImage.type === "STAGED";

  return (
    <div className={css.picker}>
      <DialogDisclosure className={css.radio({ aspectRatio: "16/9", size: "large" })}>
        <Image
          {...thumbSize}
          className={css.stagedImage({ fit: "cover" })}
          src={isStagedImageUpload ? stagedImage.src : getImageUploadSrc(stagedImage.src)}
          optimize={!isStagedImageUpload}
        />

        <div className={css.selectionFrame({ thinBorder: true })} />
      </DialogDisclosure>

      {info}
    </div>
  );
}

export function MainImagePicker(props: ReturnType<typeof useStagedImage>) {
  return (
    <DialogTrigger>
      <MainImageSelectDialog image={props.stagedImage} onSuccess={props.setStagedImage} />

      <ImagePicker {...props} />
    </DialogTrigger>
  );
}
